import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { publicPlans, type PlanLike, type PublicPlan } from '@/lib/plans';

/**
 * The plans offered on the public signup page. Read live from `subscriptionPlans` (the rules
 * allow an unauthenticated read), so a plan Ahmed deactivates in Settings leaves signup with no
 * release. Never falls back to a hard-coded list: a wrong price on a public page is worse than
 * an honest "couldn't load".
 */
export const PLANS_TIMEOUT_MS = 10_000;

export function usePublicPlans() {
    const [plans, setPlans] = useState<PublicPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let live = true;
        // Firestore retries a failed read silently rather than rejecting, so with no network the
        // promise never settles: the field would sit on "loading" for ever and the submit button
        // would stay disabled with no way out. After PLANS_TIMEOUT_MS we call it a failure, which
        // at least offers Retry. (Found while trying to screenshot the failed state.)
        const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('timed out loading the plans')), PLANS_TIMEOUT_MS));
        Promise.race([getDocs(collection(db, 'subscriptionPlans')), timeout])
            .then(snap => {
                if (!live) return;
                const offered = publicPlans(snap.docs.map(d => ({ id: d.id, ...d.data() }) as PlanLike));
                // NO PLANS IS A FAILURE, not an empty picker. Offline, Firestore resolves with an
                // empty snapshot instead of rejecting, so a visitor with no signal was shown a
                // dropdown holding nothing — the very thing the members complained about. If the
                // academy genuinely has no public plan, that is a misconfiguration Ahmed must see.
                if (offered.length === 0) { setFailed(true); setPlans([]); return; }
                setPlans(offered);
            })
            .catch(err => {
                console.error('usePublicPlans: could not load the plans', err);
                if (live) { setPlans([]); setFailed(true); }
            })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, [attempt]);

    // Reset the state here rather than at the top of the effect, which would set state
    // synchronously during render.
    const retry = useCallback(() => {
        setLoading(true);
        setFailed(false);
        setAttempt(a => a + 1);
    }, []);
    return { plans, loading, failed, retry };
}
